import { useState, useRef, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./Header.module.css";
import BurgerMenu from "../UI/BurgerMenu/BurgerMenu";
import Button from "../UI/Button/Button";
import LoadingOverlay from "../UI/LoadingOverlay/LoadingOverlay";
import softindexLogoMark from "../../assets/images/logo/softindex-logo.webp";
import { stripLangPrefix, withLangPrefix } from "../../utils/langRouting";

const HERO_SCROLL = 72;
const SCROLL_DELTA = 10;
const TOP_REVEAL = 48;

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageLoading, setIsLanguageLoading] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);
  const dropdownRef = useRef(null);
  const lastScrollY = useRef(0);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  const isHeroBackdropPage =
    stripLangPrefix(pathname) === "/" ||
    stripLangPrefix(pathname) === "/services" ||
    stripLangPrefix(pathname) === "/portfolio";
  const isHeroTop = isHeroBackdropPage && scrollY < HERO_SCROLL;
  const useCompactHomeHeader = isHeroBackdropPage && scrollY >= HERO_SCROLL;

  useEffect(() => {
    lastScrollY.current = window.scrollY || 0;
    setScrollY(lastScrollY.current);
    setHeaderVisible(true);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => {
      const current = window.scrollY || 0;
      setScrollY(current);

      if (isMobileMenuOpen) {
        setHeaderVisible(true);
        lastScrollY.current = current;
        return;
      }

      if (current < TOP_REVEAL) {
        setHeaderVisible(true);
        lastScrollY.current = current;
        return;
      }

      const prev = lastScrollY.current;
      const delta = current - prev;
      lastScrollY.current = current;

      if (delta > SCROLL_DELTA) {
        setHeaderVisible(false);
      } else if (delta < -SCROLL_DELTA) {
        setHeaderVisible(true);
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    window.scrollTo(0, 0);
    setIsMobileMenuOpen((prev) => !prev);
  };

  const handleWhyUsClick = (e) => {
    if (stripLangPrefix(window.location.pathname) === "/") {
      e.preventDefault();
      document.getElementById("why-us")?.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  const changeLanguage = (language) => {
    setIsLanguageLoading(true);
    setIsLanguageDropdownOpen(false);
    i18n.changeLanguage(language);
    navigate(withLangPrefix(stripLangPrefix(pathname), language), {
      replace: true,
    });
  };

  const handleLanguageLoadingComplete = () => {
    setIsLanguageLoading(false);
  };

  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen((prev) => !prev);
  };

  const currentLanguage = i18n.language?.startsWith("ro") ? "ro" : "en";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "ro", name: "România", flag: "🇷🇴" },
  ];

  const headerClass = [
    styles.header,
    isHeroTop && !isMobileMenuOpen ? styles.headerHero : styles.headerSolid,
    useCompactHomeHeader ? styles.headerCompact : "",
    !headerVisible ? styles.headerHidden : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <header className={headerClass}>
        <div className={styles.container}>
          <Link
            to={withLangPrefix("/", currentLanguage)}
            className={styles.logo}
            aria-label={t("header.logoHome")}
          >
            <img
              src={softindexLogoMark}
              alt="Softindex — logo"
              title="Softindex"
              width="48"
              height="48"
              decoding="async"
              className={styles.logoMark}
            />
            <span className={styles.logoWordmark}>softindex</span>
          </Link>

          <nav
            className={`${styles.nav} ${
              isMobileMenuOpen ? styles.navOpen : ""
            }`}
          >
            <Link
              to={withLangPrefix("/", currentLanguage)}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t("header.home")}
            </Link>
            <Link
              to={withLangPrefix("/services", currentLanguage)}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t("header.services")}
            </Link>
            <Link
              to={withLangPrefix("/portfolio", currentLanguage)}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t("header.portfolio")}
            </Link>
            <Link
              to={withLangPrefix("/#why-us", currentLanguage)}
              onClick={handleWhyUsClick}
            >
              {t("header.whyUs")}
            </Link>
            <span className={styles.navButtonWrap}>
              <Button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate(withLangPrefix("/contact", currentLanguage));
                }}
              >
                {t("header.contactUs")}
              </Button>
            </span>

            <div className={styles.languageSwitcher} ref={dropdownRef}>
              <button
                type="button"
                onClick={toggleLanguageDropdown}
                className={styles.languageButton}
                aria-label="Select Language"
                aria-expanded={isLanguageDropdownOpen}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={styles.languageIcon}
                  aria-hidden
                >
                  <path
                    d="M12.87 15.07L10.33 12.56L10.36 12.53C12.1 10.59 13.34 8.36 14.07 6H17V4H10V2H8V4H1V6H12.17C11.5 7.92 10.44 9.75 9 11.35C8.07 10.32 7.3 9.19 6.69 8H4.69C5.42 9.63 6.42 11.17 7.67 12.56L2.58 17.58L4 19L9 14L12.11 17.11L12.87 15.07ZM18.5 10H16.5L12 22H14L15.12 19H19.87L21 22H23L18.5 10ZM15.88 17L17.5 12.67L19.12 17H15.88Z"
                    fill="currentColor"
                  />
                </svg>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`${styles.dropdownArrow} ${
                    isLanguageDropdownOpen ? styles.dropdownArrowOpen : ""
                  }`}
                  aria-hidden
                >
                  <path d="M7 10L12 15L17 10H7Z" fill="currentColor" />
                </svg>
              </button>

              {isLanguageDropdownOpen && (
                <div className={styles.languageDropdown}>
                  {languages.map((language) => (
                    <button
                      type="button"
                      key={language.code}
                      onClick={() => changeLanguage(language.code)}
                      className={`${styles.languageOption} ${
                        currentLanguage === language.code
                          ? styles.languageOptionActive
                          : ""
                      }`}
                    >
                      <span className={styles.languageFlag}>
                        {language.flag}
                      </span>
                      <span className={styles.languageName}>
                        {language.name}
                      </span>
                      {currentLanguage === language.code && (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className={styles.checkIcon}
                          aria-hidden
                        >
                          <path
                            d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
                            fill="currentColor"
                          />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className={styles.mobileMenuButton}>
            <BurgerMenu
              isOpen={isMobileMenuOpen}
              toggleMenu={toggleMobileMenu}
              light={isHeroTop && !isMobileMenuOpen}
            />
          </div>
        </div>
      </header>

      <LoadingOverlay
        isVisible={isLanguageLoading}
        onComplete={handleLanguageLoadingComplete}
      />
    </>
  );
}
